<template>
  <ul class="filter-bar">
    <li v-if="$fetchState.error" class="callout callout-error">{{ $fetchState.error.message }}</li>
    <li
      v-for="filter in dropdownFilters"
      :key="filter"
      :class="{ filter: true, active: isActive(filter) }"
    >
      <dropdown-menu
        type="chip caret"
        :disabled="availableItems[filter].length === 0"
        :aria-disabled="availableItems[filter].length === 0"
      >
        <template #label>
          <span>{{ activeFilterLabel(filter) }}</span>
        </template>

        <ul v-if="availableItems[filter]" class="link-list">
          <template v-for="(item, idx) in availableItems[filter]">
            <resource-list-filters-child
              v-if="item.slug && item.list"
              :key="`${item.slug}-${idx}`"
              :active-item="activeItems[filter] ? activeItems[filter][0] : null"
              :category="filter"
              :item="item"
              @toggle-filter="toggleHierarchicalFilter"
            />
            <li v-else :key="`${item.name}-${idx}`">
              <div>
                <input
                  :id="'filter-' + item.slug"
                  v-model="activeItems[filter]"
                  type="checkbox"
                  :value="item.slug"
                  :disabled="item.count === 0"
                  :aria-disabled="item.count === 0"
                  @change="toggleFilter(filter)"
                />
                <label :for="'filter-' + item.slug">
                  <span>{{ item.name }}</span>
                  <span class="label pull-right">{{ numberWithCommas(item.count) }}</span>
                </label>
              </div>
            </li>
          </template>
        </ul>
      </dropdown-menu>
    </li>
    <li
      v-if="availableFilters.length > 3"
      :class="{ 'filter-more': true, active: moreFiltersActiveCount > 0 }"
    >
      <dropdown-menu ref="moreFilters" type="chip caret" position="left">
        <template #label>
          <span>More filters</span>
          <template v-if="moreFiltersActiveCount > 0">({{ moreFiltersActiveCount }})</template>
        </template>

        <ul class="no-list filter-more-list">
          <li>
            <h5>Location</h5>

            <button class="button button-link" @click="toggleLocationSelectorModal(true)">
              <nuxt-img
                :src="
                  location.code ? '/images/flags/' + location.code + '.svg' : '/images/flags/au.svg'
                "
                alt=""
                height="30"
                width="40"
                class="icon icon-small"
              />
              &nbsp;
              {{ location.name || $i18n.locales.find(l => l.code === $i18n.locale).name }}
            </button>
          </li>

          <li
            v-for="(filter, idx) in moreFilters"
            :key="`${filter}-${idx}`"
            :class="{ filter: true, active: isActive(filter) }"
          >
            <h5>{{ filter.replace(/_/g, ' ') }} {{ activeFilterCount(filter) }}</h5>

            <ul
              v-if="availableItems[filter] && availableItems[filter].length > 0"
              class="link-list"
            >
              <li
                v-for="(item, itemIdx) in availableItems[filter]"
                v-if="item.slug"
                :key="`${item.slug}-${idx}-${itemIdx}`"
              >
                <div>
                  <input
                    :id="'filter-' + item.slug"
                    v-model="activeItems[filter]"
                    type="checkbox"
                    :value="item.slug"
                    @change="toggleFilter(filter)"
                  />
                  <label :for="'filter-' + item.slug">
                    <span>{{ item.name }}</span>
                    <span class="label pull-right">{{ numberWithCommas(item.count) }}</span>
                  </label>
                </div>
              </li>
            </ul>
            <small v-else class="text-mute padded">No filters found.</small>
          </li>
        </ul>
      </dropdown-menu>
    </li>
    <li>
      <button title="Clear all filters" class="clear" @click="clearFilters">Clear</button>
    </li>
    <li class="pull-right">
      <span data-tooltip="Sort by relevance">
        <button
          v-if="listType === 'search'"
          title="Sort by relevance"
          :class="{
            chip: true,
            'button-icon': true,
            active: orderBy === 'score',
          }"
          @click="updateOrderBy('score')"
        >
          <svg class="icon" role="img" aria-label="Relevance">
            <use xlink:href="/images/fa-icons.svg#list-numbers"></use>
          </svg>
        </button>
      </span>
      <span data-tooltip="Sort by recent">
        <button
          title="Sort by recent"
          :class="{
            chip: true,
            'button-icon': true,
            active:
              listType === 'search' ? orderBy === 'modified' : orderBy !== 'popularity_1_week',
          }"
          @click="updateOrderBy('modified')"
        >
          <svg class="icon" role="img" aria-label="Recent">
            <use xlink:href="/images/fa-icons.svg?v=4#clock"></use>
          </svg>
        </button>
      </span>
      <span data-tooltip="Sort by popularity" data-position="left bottom">
        <button
          title="Sort by popularity"
          :class="{
            chip: true,
            'button-icon': true,
            active: orderBy === 'popularity_1_week',
          }"
          @click="updateOrderBy('popularity_1_week')"
        >
          <svg class="icon" role="img" aria-label="Popularity">
            <use xlink:href="/images/fa-icons.svg?v=4#fire"></use>
          </svg>
        </button>
      </span>
    </li>
  </ul>
</template>

<script>
import isEqual from 'lodash.isequal';
import { mapGetters } from 'vuex';
import DropdownMenu from '~/components/DropdownMenu.vue';

import debounce from '~/utils/debounce';
import { numberWithCommas } from '~/utils/formatStrings';

export default {
  name: 'ResourceListFilters',
  components: {
    DropdownMenu,
    ResourceListFiltersChild: () => import('~/components/resource/ResourceListFiltersChild.vue'),
  },
  props: {
    availableFilters: {
      type: Array,
      default: () => {
        return [];
      },
    }, // A list of types to filter by eg. [types, resource_types, year_levels]
    defaultParams: {
      type: Object,
      default() {
        return {};
      },
    }, // Contains the root, category, and default selected filter options
    filterDropdownCount: { type: Number, default: 2 }, // Customise the number of filter dropdowns shown
    searchQuery: { type: String, default: '' },
    listType: { type: String, default: 'resources' },
    orderBy: { type: String, default: 'popularity_1_week' },
  },
  data() {
    return {
      activeItems: {}, // A copy of availableItems that holds the currently selected items
      availableItems: {}, // Populated from the API endpoint
      curriculumSearch: '', // Search within curriculum codes
      moreFilters: this.availableFilters.slice(
        this.filterDropdownCount,
        this.availableFilters.length
      ), // Move all the filters after the X one into a More Filters dropdown
      moreFiltersActiveCount: 0, // A count of active filters in the More Filters dropdown
      dropdownFilters: this.availableFilters.slice(0, this.filterDropdownCount), // The first X filters are dropdowns
      error: null,
    };
  },
  async fetch() {
    // Called when component loads
    if (this.availableFilters.length > 0) {
      this.initFilterArrays();
      // Use the params from the URL query
      if (this.$route.query) {
        this.activeItems = Object.assign(this.activeItems, this.$route.query);
      }
      await this.loadFilters(this.availableFilters);
    }
  },
  computed: {
    ...mapGetters(['location']),
  },
  watch: {
    searchQuery(newVal, oldVal) {
      // Update filters when search query changes
      if (newVal !== oldVal) {
        this.$fetch();
      }
    },
    location(to, from) {
      if (isEqual(to, from)) {
        return;
      }

      if (this.$route.query) {
        this.$router.replace({ query: undefined });
      }
    },
    async '$route.query'(to) {
      // If the query and active items are the same, that means
      // that it has been triggered in the filter dropdowns and we don't need to fetch again.
      if (isEqual(to, this.activeItems)) {
        return;
      }

      // Fetch again if the route query has changed outside of a dropdown interaction
      await this.$fetch();
      this.$emit('update-filters', this.activeItems);
    },
  },
  methods: {
    numberWithCommas,
    initFilterArrays() {
      if (this.availableFilters && this.availableFilters.length > 0) {
        // Init the empty availableItems objects
        this.availableItems = this.availableFilters.reduce((ac, a) => ({ ...ac, [a]: [] }), {});

        // Make a copy of availableItems to record which filters are selected
        this.activeItems = JSON.parse(JSON.stringify(this.availableItems));

        // Re-add search query after clearing object
        if (this.searchQuery !== '') {
          this.activeItems.query = this.searchQuery;
        }
      }
    },
    async loadFilters(filters = {}, refresh) {
      // Request list of filters from the API
      let params = {
        country: this.defaultParams.country ? this.defaultParams.country : this.$i18n.locale,
        filters, // Array of filters to show
      };

      // Add active selected filter items eg. { learning_areas: [] }
      params = Object.assign(params, this.activeItems);

      // Add resource type, if set eg. unit-plan, lesson-plan
      if (this.defaultParams.types) {
        params.types = this.defaultParams.types;
      }

      // Add root for topic pages, if set eg. learning-areas
      if (this.defaultParams.root) {
        params.root = this.defaultParams.root;
      }

      // Optional category for topic pages eg. vocabulary
      if (this.defaultParams.category) {
        params.category = this.defaultParams.category;
      }

      // Remap params for API: query -> search
      if (this.searchQuery) {
        params.search = this.searchQuery;
        delete params.query;
      }

      // Search within curriculum codes
      if (this.curriculumSearch && this.curriculumSearch !== '') {
        params.search_curriculums = this.curriculumSearch;
      }

      try {
        const response = await this.$store.dispatch('loadFilters', params);
        // Update the filter dropdowns
        this.availableItems = Object.assign(this.availableItems, response);
        // Check if filter is inside the More Filters list and update the count
        this.updateMoreFiltersCount();

        if (refresh) {
          const { search: _, ...query } = this.activeItems;

          // Update URL params
          this.$router.replace({ query }, () => {});
        }
      } catch (err) {
        console.error(err);
        //this.error = 'Filters failed to load';
      }

      return null;
    },
    clearFilters() {
      // Reset all filters and load them again
      this.initFilterArrays();
      this.$emit('update-filters');
      this.loadFilters(this.availableFilters, true);
    },
    isActive(category) {
      // Highlight the filter if one or more items selected
      return this.activeItems[category] && this.activeItems[category].length > 0;
    },
    activeFilterCount(category) {
      // Return the number of items selected in a filter
      const items = this.activeItems[category];
      return items && items.length > 0 ? '(' + items.length + ')' : '';
    },
    activeFilterLabel(category) {
      // Return label depending on how many items are selected
      const items = this.activeItems[category];

      // Get translation if it exists
      const transformedCategory = this.$te(`${category}_category_label`)
        ? this.$t(`${category}_category_label`)
        : category.replace(/_/g, ' ');

      if (items && items.length === 1) {
        // If one item, label is: "Selected Item Name"
        if (category === 'curriculum') {
          return items[0]
            .replace(/-/g, ' ')
            .replace(/ au| gb| us/gi, '')
            .toUpperCase();
        } else {
          return (
            this.availableItems[category]?.find(item => item.slug === items[0])?.name ||
            items[0].replace(/-/g, ' ').replace(/ au| gb| us/gi, '')
          );
        }
      } else if (items && items.length > 1) {
        // If more than one item, label is: "Category Name (Count)""
        return transformedCategory + ' (' + items.length + ')';
      }

      // Label is: "Category Name"
      return transformedCategory;
    },
    toggleHierarchicalFilter(item, category) {
      // For hierarchical filter
      if (item) {
        this.activeItems[category] = [item];
      } else {
        this.activeItems[category] = [];
      }

      this.toggleFilter(category);
    },
    toggleFilter(category) {
      // Make a new request every time a filter is selected/deselected
      // Reload the other filters with new values based on the active items
      const filtersToUpdate = this.availableFilters.filter(f => f !== category);
      this.loadFilters(filtersToUpdate, true);
      // Send params to parent to request new list of resources
      this.$emit('update-filters', this.activeItems);
    },
    updateOrderBy(orderBy = 'popularity_1_week') {
      this.$emit('update-order-by', orderBy);
    },
    updateMoreFiltersCount() {
      // More Filters label count
      let i = this.filterDropdownCount;
      this.moreFiltersActiveCount = 0;

      if (this.availableFilters[this.filterDropdownCount]) {
        for (; i < this.availableFilters.length; i++) {
          this.moreFiltersActiveCount += this.activeItems[this.availableFilters[i]].length;
        }
      }
    },
    showChildFilters(filter, slug, itemList) {
      if (itemList && itemList.length) {
        if (
          this.activeItems[filter].includes(slug) ||
          itemList.filter(i => this.activeItems[filter].includes(i.slug)).length > 0
        ) {
          return true;
        }
      }
      return false;
    },
    searchCurriculum: debounce(function () {
      // Start searching after 2 characters
      this.loadFilters(this.availableFilters);
    }, 300),
    toggleLocationSelectorModal(show) {
      this.$store.dispatch('toggleLocationSelectorModal', show);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~/assets/scss/components/_form.scss';
@import '~/assets/scss/components/_filter.scss';
.clear {
  font-size: $font-size-300;
  line-height: 1.5rem;
  font-weight: bold;
  color: $link-blue-500;
  opacity: 0.4;
  margin-left: $base-padding-two-thirds * 2;
}
.chip {
  line-height: 0;
}
</style>
